import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Link1Icon, LockClosedIcon, TrashIcon } from '@radix-ui/react-icons';
import { Switch } from '@producer/ui-kit';

import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { formatDate } from '../../../lib/utils/DateHelper';
import { dateShort } from '../../../assets/contants/dates-fns';
import { Asset } from '../../../app/entities/asset';
import { Space } from '../../../app/entities/space';
import { ShareLinkPasswordDialog } from './share-link-password-dialog';

import type { ShareLink } from '../../../app/entities/share-link';

import styles from './share-link-item.module.scss';
type ShareLinkItemProps = {
  link: ShareLink;
};

export const ShareLinkItem: React.FC<ShareLinkItemProps> = observer(({ link }) => {
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);

  const space = Space.getOne(link.spaceId);
  const isPasswordRequired = !!space?.requiresSharePassword;
  const isProLocked = !space?.hasSubscription();

  const menuItems = [
    {
      onSelect: () => navigator.clipboard.writeText(link.url),
      title: 'Copy link',
      icon: <Link1Icon />,
    },
    {
      onSelect: () => setIsPasswordDialogOpen(true),
      title: 'Password…',
      icon: <LockClosedIcon />,
    },
    {
      onSelect: () => link.delete(),
      title: 'Delete',
      icon: <TrashIcon />,
      type: 'danger' as const,
    },
  ];

  const hasDownloadableItems = link.assetIds
    .map((id) => Asset.getOne(id) || { type: 'file' })
    .some((asset) => asset!.type !== 'link');

  return (
    <tr className={styles['row']}>
      <td className={styles['cell']}>
        <div className={styles['name']}>
          <span>{link.name}</span>

          {link.hasPassword && (
            <span className={styles['badge']}>
              <LockClosedIcon />
              Password
            </span>
          )}

          {!link.hasPassword && isPasswordRequired && (
            <span className={styles['badge']} data-variant="warning">
              No password
            </span>
          )}
        </div>
      </td>
      <td className={styles['cell']}>{formatDate(link.createdAt.toString(), dateShort)}</td>
      <td className={styles['cell']}>{link.assetIds?.length || 0}</td>
      <td className={styles['cell']}>{link.creatorName}</td>
      <td className={styles['cell']} style={{ textAlign: 'center' }}>
        {hasDownloadableItems && (
          <Switch
            onToggle={(canDownload) => link.update({ canDownload })}
            isToggled={!!link.settings.canDownload}
            size="medium"
          />
        )}
      </td>

      <td className={styles['cell']}>
        <DropdownMenu items={menuItems} />

        {isPasswordDialogOpen && (
          <ShareLinkPasswordDialog
            link={link}
            isPasswordRequired={isPasswordRequired}
            isProLocked={isProLocked}
            onClose={() => setIsPasswordDialogOpen(false)}
          />
        )}
      </td>
    </tr>
  );
});
